/**
 * PastDueBanner
 * Global banner shown above the header when billing status is past_due.
 * Only visible to project owners. Redirects to billing portal on "Pay now".
 *
 * @see BILLING_IMPLEMENTATION_GUIDE.md
 */

import React from "react";
import { useTranslation } from "react-i18next";
import {
  useBillingRedirect,
  usePastDueBannerVisible,
} from "../../../hooks/useBilling";
import { useUserStore } from "@finitless/shared/stores";
import { useToastContext } from "@finitless/shared/contexts";
import { Icon } from "../UIElements/Icon";

/** Approximate banner height for layout padding (px) */
export const PAST_DUE_BANNER_HEIGHT = 80;

const PastDueBanner = () => {
  const { t } = useTranslation(["billing"]);
  const toast = useToastContext();
  const user = useUserStore((state) => state.user);
  const showBanner = usePastDueBannerVisible();
  const { redirectToBillingPortal, isRedirecting } = useBillingRedirect();

  if (!showBanner) {
    return null;
  }

  const userName = user?.name || user?.email || "there";

  const handlePayNow = () => {
    redirectToBillingPortal().catch((err) =>
      toast.error(err?.message || t("redirectFailed")),
    );
  };

  return (
    <div
      className="fixed inset-x-0 top-0 z-[10000] flex items-center justify-between gap-4 bg-red-600 px-4 py-3 text-white shadow-lg sm:px-6"
      role="alert"
      aria-live="assertive"
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Icon
          name="AlertTriangle"
          className="h-6 w-6 flex-shrink-0 text-white"
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <p className="font-semibold">
            {t("pastDueTitle", { user: userName })}
          </p>
          <p className="mt-0.5 text-sm opacity-95">{t("pastDueSubtitle")}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={handlePayNow}
        disabled={isRedirecting}
        className="flex-shrink-0 rounded-md bg-white px-4 py-2 font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-70"
      >
        {isRedirecting ? (
          <span className="flex items-center gap-2">
            <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
            {t("paying")}
          </span>
        ) : (
          t("payNow")
        )}
      </button>
    </div>
  );
};

export default PastDueBanner;
